import { FC, useContext, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import useSWR from 'swr';

import { CloudinaryImage } from 'components/common/CloudinaryImage';
import { ContentLoader } from 'components/design-system/ContentLoader';
import { SlideoutCommonProps } from 'components/design-system/Slideout';
import { SellerProfileContext } from 'context/sellerProfile';
import { clientApiGet } from 'lib/api_helper';
import { pageRoutes } from 'lib/routes';
import { buildListingUrl } from 'lib/url_helpers';
import styles from 'styles/PurchaseRequest.module.scss';
import { Listing, ListingImageType, PurchaseRequest } from 'types';
import { formatPrice } from 'utils/text-helpers';

import { PurchaseRequestSlideout } from './PurchaseRequestSlideout';

type SellerInventorySlideoutProps = {
  purchaseRequest: PurchaseRequest;
} & SlideoutCommonProps;

export const SellerInventorySlideout: FC<SellerInventorySlideoutProps> = ({ purchaseRequest, ...props }) => {
  const { sellerProfile } = useContext(SellerProfileContext);
  const { push } = useRouter();

  const { data: listings = [], isLoading } = useSWR<Listing[]>(
    sellerProfile
      ? `/api/purchaseRequests/request?purchaseRequestId=${purchaseRequest.id}&sellerId=${sellerProfile.id}&expand[]=model`
      : null,
    clientApiGet
  );

  const filteredListings = useMemo(
    () => listings.filter(({ activePricingModel }) => activePricingModel === 'FIXED_PRICE'),
    [listings]
  );

  const onCreateNewListing = () => {
    push(
      `${pageRoutes.sellerCreateNewListing}?modelId=${purchaseRequest.model.id}&purchaseRequest=${purchaseRequest.id}`
    );
    props.closeModal?.();
  };

  return (
    <PurchaseRequestSlideout
      title='Your inventory'
      subtitle={
        <div className='d-flex'>
          {isLoading ? (
            <ContentLoader style={{ marginRight: '5px' }} className='' width={25} />
          ) : (
            filteredListings?.length
          )}{' '}
          MATCHING EXAMPLE
          {filteredListings.length > 1 ? 'S' : ''}
        </div>
      }
      cancel={{
        textClassname: 'w-100',
        title: 'Add a new listing',
        onClick: onCreateNewListing
      }}
      {...props}
    >
      <div className='d-flex flex-column w-100 align-items-center'>
        {isLoading && (
          <div className='w-100'>
            <ContentLoader style={{ height: 152 }} borderRadius={20} />
          </div>
        )}
        {filteredListings.length === 0 && !isLoading && (
          <div className='fs-14px px-4 riforma-regular text-center mt-6'>
            No matching watches found in your inventory. You add new watch by clicking button below
          </div>
        )}
        {filteredListings.map(listing => {
          const url = buildListingUrl(
            listing.model.brand.displayName,
            listing.model.displayName,
            listing.model.referenceNumber,
            listing.id
          );
          const imageUrl =
            listing.images.find(i => i.type === ListingImageType.FRONT)?.image.optimizedUrl || 'model_placeholder.png';

          let accessories = '';

          if (listing.accessories.length > 0) {
            accessories += 'with';
          }

          listing.accessories.forEach(
            (accessory, index) => (accessories += `${index > 0 ? ',' : ''} ${accessory.name}`)
          );

          return (
            <Link
              onClick={props.closeModal}
              href={url + `?purchaseRequest=${purchaseRequest.id}`}
              role='button'
              className={classNames(
                styles.selectionItem,
                'd-flex my-1 w-100 justify-content-between p-1 rounded-2 border border-primary12 text-primary'
              )}
              style={{ height: 152 }}
              key={listing.id}
            >
              <div className='d-flex h-100 w-60 flex-column justify-content-between p-3'>
                <div>
                  <div className='riforma-regular text-success fs-12px'>Active</div>
                  <div className='fs-14px'>{listing.model.displayName}</div>
                </div>
                <div className='riforma-regular fs-12px opacity-60'>
                  {formatPrice(listing.priceCents)}
                  {listing.manufactureYear ? ` / ${listing.manufactureYear}` : ''} / {listing.condition}
                </div>
              </div>
              <div className='w-40 h-100 position-relative border-radius-16 overflow-hidden'>
                <CloudinaryImage
                  fill
                  style={{ objectFit: 'cover' }}
                  src={imageUrl}
                  alt={`${listing.model.displayName} image`}
                />
              </div>
            </Link>
          );
        })}
      </div>
    </PurchaseRequestSlideout>
  );
};
